import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Typography } from "@mui/material";
import moment from "moment";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../components/CustomTextField";
import CustomSelect from "../../../components/Select/Select";
import { createStockIssue } from "../../../redux/reducers/clinicSlice";
import { addIcon, delBtn } from "../../../util/CommonIconStyle";
import { requestTypeList } from "../../../util/dropList";

const initVal = {
  requestType: requestTypeList[1],
  requestTypeError: false,
  location: null,
  locationErr: false,
  requestedBy: null,
  requestedDate: new Date(),
  requestedByErr: false,
};
const reqTxt = "This field is required";
const initObj = {
  itemName: null,
  itemNameErr: false,
  name: null,
  quantity: null,
  nameErr: false,
  qtyErr: false,
  issQtyErr: false,
  issueQuantity: null,
  fromLocation: null,
  fromLocationErr: false,
  toLocation: null,
  toLocationErr: false,
  batchNumber: null,
  batchErr: false,
};
const requestAllowedCategories = [
  "medication",
  "diet",
  "nutrition and supplements",
  "fluid therapy",
  "other parasite treatment",
  "deworming",
  "flea & tick treatment",
];

const StockRequestModal = ({
  modVsble,
  handleModClose = () => {},
  selectedItem = null,
  issueModVsble = false,
}) => {
  const dispatch = useDispatch();
  const [modValues, setModValues] = useState(initVal);
  const [addedList, setAddedList] = useState([{ ...initObj, i: 0 }]);
  const {
    stockIssues,
    clinicStocks,
    clinicRoles,
    locationList,
    facilities,
    details: clinicDet,
  } = useSelector((state) => state?.clinic);
  const vets = useSelector((state) => state?.vet?.vets);
  const role = localStorage.getItem("role");
  const user = localStorage.getItem("user");
  const profile = JSON.parse(user);

  const getLocationsByRequestType = (typ = null) => {
    if (locationList?.length === 0 || !locationList) return [];
    const reqTypes =
      typ === "Consumption" ? ["consumption"] : ["warehouse", "storage"];
    return locationList
      ?.filter((li) => reqTypes?.includes(li?.locationType?.toLowerCase()))
      ?.map((loc) => ({
        name: loc?.locationName,
        value: loc?.locationName,
        id: loc?.id,
      }));
  };

  const handleChange = (name, value) => {
    const reqObj = { ...modValues, [name]: value };
    if (name === "location") {
      reqObj.locationErr = value ? false : true;
    }
    if (name === "requestType") {
      reqObj.requestTypeError = value ? false : true;
      reqObj.location = null;
      setAddedList([{ ...initObj, i: 0 }]);
    }
    if (name === "requestedBy") {
      reqObj.requestedByErr = value ? false : true;
    }
    setModValues(reqObj);
  };

  const getPresList = () => {
    if (
      !clinicStocks?.data ||
      clinicStocks?.data?.length === 0 ||
      !modValues?.requestType?.value ||
      !modValues?.location?.id
    )
      return [];
    const reqList = clinicStocks?.data?.filter((dta) => {
      const category = (dta?.category ?? "")?.toLowerCase()?.trim();
      const conMatch =
        modValues?.requestType?.value?.toLowerCase() === "consumption"
          ? dta?.autoConsumed
          : !dta?.autoConsumed;
      return (
        dta?.billable &&
        conMatch &&
        requestAllowedCategories?.includes(category)
      );
    });
    return reqList?.map((li) => ({
      name: li?.tradeName,
      value: li?.tradeName,
      stockId: li?.stockId,
      type: li?.category,
      productName: li?.productName,
    }));
  };

  const getVetList = () => {
    const reqList = vets?.vets?.map((li) => ({
      name: li?.name,
      value: li?.doctorId,
      department: li?.department,
    }));
    if (role === "ROLE_CLINIC") {
      reqList.unshift({
        name: `${clinicDet?.name} (Clinic)`,
        value: clinicDet?.id,
        id: clinicDet?.id,
      });
    }
    return reqList;
  };

  const getBatchList = (stockId = null) => {
    if (!stockId) return [];
    return clinicStocks?.data
      ?.find((stc) => stc?.stockId === stockId)
      ?.stockBatch?.map((bat) => {
        const reqLocation = locationList?.find(
          (loc) => loc?.id === bat?.locationId,
        );
        return {
          name: bat?.batchNo,
          value: bat?.batchNo,
          id: bat?.batchId,
          subunits: bat?.subunits,
          quantity: bat?.quantity,
          locationId: bat?.locationId,
          locationName: reqLocation?.locationName ?? "",
        };
      });
  };

  const onChange = (name, value, i, stockId) => {
    const reqList = [...addedList];
    const reqObj = {
      ...reqList[i],
      [name]: value,
      itemNameErr:
        name === "itemName"
          ? value === "" || value?.length === 0 || !value
          : reqList[i]?.itemNameErr,
      nameErr:
        name === "name"
          ? value === "" || value?.length === 0 || !value
          : reqList[i]?.nameErr,
      qtyErr:
        name === "quantity"
          ? value === "" || value?.length === 0 || !value || Number(value) < 0
          : reqList[i]?.qtyErr,
      issQtyErr:
        name === "issueQuantity"
          ? value === "" || value?.length === 0 || !value || Number(value) < 1
          : reqList[i]?.issQtyErr,
      batchErr:
        name === "batchNumber"
          ? value === "" || value?.length === 0 || !value
          : reqList[i]?.batchErr,
      fromLocationErr:
        name === "fromLocation"
          ? value === "" || value?.length === 0 || !value
          : reqList[i]?.fromLocationErr,
      toLocationErr:
        name === "toLocation"
          ? value === "" || value?.length === 0 || !value
          : reqList[i]?.toLocationErr,
    };
    if (name === "name" && stockId) {
      reqObj.stockId = stockId;
      reqObj.batchNumber = getBatchList(stockId)?.[0] ?? null;
    }
    reqList[i] = reqObj;
    setAddedList(reqList);
  };

  const handleAddNew = () => {
    setAddedList([...addedList, { ...initObj, i: addedList?.length }]);
  };

  const getBatchById = (stckId = null, batId = null) => {
    if (!stckId || !batId) return "-";
    const batchList = clinicStocks?.data?.find(
      (stc) => stc?.stockId === stckId,
    )?.stockBatch;
    return batchList?.find((li) => li?.batchId === batId) ?? null;
  };

  const handleDeleteItem = (i) => {
    const reqList = addedList
      ?.filter((li) => li?.i !== i)
      ?.map((li, i) => ({ ...li, i }));
    setAddedList(reqList);
  };

  const getStockBasedQtyWithToLocation = (stckId = null) => {
    if (
      !stckId ||
      (issueModVsble ? !selectedItem?.locationId : !modValues?.location?.id)
    )
      return "-";
    const batchList = clinicStocks?.data?.find(
      (stc) => stc?.stockId === stckId,
    )?.stockBatch;
    const reqQty =
      batchList
        ?.filter(
          (li) =>
            li?.locationId ===
            (issueModVsble
              ? selectedItem?.locationId
              : modValues?.location?.id),
        )
        ?.reduce((sum, item) => sum + (item?.subunits || 0), 0) ?? "-";
    return reqQty;
  };

  const handleValidate = () => {
    const reqList = addedList?.map((li) => {
      const reqObj = {
        ...li,
        nameErr: li?.name === "" || li?.name?.length === 0 || !li?.name,
        qtyErr:
          li?.quantity === "" ||
          li?.quantity?.length === 0 ||
          !li?.quantity ||
          Number(li?.quantity) < 0,
        batchErr: !li?.batchNumber?.id,
      };
      return reqObj;
    });
    setAddedList(reqList);
    return reqList?.some((li) => li?.nameErr || li?.qtyErr || li?.batchErr);
  };

  const handleSave = () => {
    // if (role === "ROLE_CLINIC") {
    if (
      !modValues?.requestType?.value ||
      !modValues?.location?.id ||
      (role === "ROLE_CLINIC" ? !modValues?.requestedBy?.value : false)
    ) {
      setModValues({
        ...modValues,
        requestTypeError: !modValues?.requestType?.value,
        locationErr: !modValues?.location?.id,
        requestedByErr:
          role === "ROLE_CLINIC" ? !modValues?.requestedBy?.value : false,
      });
      return;
    }
    // }
    if (handleValidate() || addedList?.length === 0) return;
    const stockIssueDetails = addedList?.map((li) => {
      const batchList = clinicStocks?.data?.find(
        (stc) => stc?.stockId === li?.stockId,
      )?.stockBatch;
      const reqBatch = li?.batchNumber?.id
        ? batchList?.find((bat) => bat?.batchId === li?.batchNumber?.id)
        : batchList?.[0];
      // const reqBatch = li?.batchNumber?.id
      //   ? batchList?.find((bat) => bat?.locationId === li?.batchNumber?.id)
      //   : li?.fromLocation?.id
      //   ? batchList?.find((bat) => bat?.locationId === li?.fromLocation?.id)
      //   : batchList?.[0];
      const reqObj = {
        itemName: li?.name ?? "",
        quantity: Number(li?.quantity ?? 1),
        status:
          role === "ROLE_CLINIC"
            ? li?.stockId
              ? "Completed"
              : "Pending"
            : "Pending",
        stockId: li?.stockId ?? null,
        batchId: batchList?.length > 0 ? (reqBatch?.batchId ?? null) : null,
        mode: "manual",
        isRejected: false,
        toLocationName: modValues?.location?.name ?? null,
        toLocationId: modValues?.location?.id ?? null,
      };
      if (role === "ROLE_CLINIC") {
        const reqBatch = getBatchById(li?.stockId, li?.batchNumber?.id);
        reqObj.issueQuantity = Number(li?.quantity ?? 1);
        reqObj.fromLocationName = reqBatch?.location ?? null;
        reqObj.fromLocationId = reqBatch?.locationId ?? null;
        // reqObj.toLocationName = modValues?.facility?.name ?? null;
        // reqObj.toLocationId = modValues?.facility?.primaryStorage ?? null;
      }
      return reqObj;
    });
    const reqObj = {
      stockIssueDetails,
      locationId: modValues?.location?.id,
      requestType: modValues?.requestType?.value,
      requestedDate: moment(new Date(modValues?.requestedDate)).format(
        "YYYY-MM-DD",
      ),
      requestedBy: modValues?.requestedBy?.value ?? profile?.id ?? null,
    };
    dispatch(createStockIssue(reqObj)).then((res) => {
      if (res?.payload) {
        handleModClose("apiSuccess");
      }
    });
  };

  return (
    <>
      <CustomModal
        open={modVsble}
        onClose={() => handleModClose("close")}
        header="Create"
        modalWidth={70}
        modal
      >
        <div className="scroll-60vh">
          <div className="flex-row">
            <div className="w23Per">
              <Typography className="font-bold blue-color fs12 mb5">
                Request Type <span className="red-color">*</span>
              </Typography>
              <CustomSelect
                list={requestTypeList}
                value={modValues?.requestType}
                error={modValues?.requestTypeError}
                helperText={
                  modValues?.requestTypeError ? "This is required field" : ""
                }
                select
                handleChange={(e, value) => {
                  handleChange("requestType", value);
                }}
                search
              />
            </div>
            <div className="w23Per ml15">
              <Typography className="font-bold blue-color fs12 mb5">
                Location <span className="red-color">*</span>
              </Typography>
              <CustomSelect
                list={getLocationsByRequestType(
                  modValues?.requestType?.value ?? null,
                )}
                value={modValues?.location}
                error={modValues?.locationErr}
                helperText={
                  modValues?.locationErr ? "This is required field" : ""
                }
                select
                handleChange={(e, value) => {
                  handleChange("location", value);
                }}
                search
              />
            </div>
            {role === "ROLE_CLINIC" ? (
              <>
                <div className="w23Per ml15">
                  <Typography className="font-bold blue-color fs12 mb5">
                    Requested By <span className="red-color">*</span>
                  </Typography>
                  <CustomSelect
                    list={getVetList()}
                    value={modValues?.requestedBy}
                    error={modValues?.requestedByErr}
                    helperText={
                      modValues?.requestedByErr ? "This is required field" : ""
                    }
                    select
                    handleChange={(e, value) => {
                      handleChange("requestedBy", value);
                    }}
                    search
                  />
                </div>
                <div className="w23Per ml15">
                  <Typography className="font-bold blue-color fs12 mb5">
                    Request Date <span className="red-color">*</span>
                  </Typography>
                  <CustomTextField
                    value={modValues?.requestedDate}
                    handleChange={(e) => {
                      handleChange("requestedDate", e?.target?.value);
                    }}
                    mobileDate
                    fullWidth
                  />
                </div>
              </>
            ) : null}
          </div>

          <div className="flex-row-ali-cen ph20 mt20">
            <div className={role === "ROLE_CLINIC" ? "w30Per" : "w70Per"}>
              <Typography className="font-bold blue-color fs12">
                Product Name <span className="red-color">*</span>
              </Typography>
            </div>
            {role === "ROLE_CLINIC" ? (
              <div className="w20Per">
                <Typography className="font-bold blue-color fs12">
                  Batch No. <span className="red-color">*</span>
                </Typography>
              </div>
            ) : null}
            {role === "ROLE_CLINIC" ? (
              <>
                <div className="w15Per">
                  <Typography className="font-bold blue-color fs12">
                    Available Qty @ Location
                  </Typography>
                </div>
                <div className="w12Per">
                  <Typography className="font-bold blue-color fs12">
                    From Location
                    {/* <span className="red-color">*</span> */}
                  </Typography>
                </div>
              </>
            ) : null}
            <div className={role === "ROLE_CLINIC" ? "w18Per" : "w25Per"}>
              <Typography className="font-bold blue-color fs12">
                {role === "ROLE_CLINIC" ? "Issuing" : "Requesting"} Qty
                <span className="red-color">*</span>
              </Typography>
            </div>
            <div className="w5Per" />
          </div>
          <div className="blue-hor-split mt5" />
          {addedList?.map((al, i) => {
            return (
              <div
                className="flex-row ph20 mt10 plan-bor"
                key={`iss_${al?.i ?? i}`}
              >
                <div
                  className={`${
                    role === "ROLE_CLINIC" ? "w30Per" : "w70Per"
                  } pr10`}
                >
                  <CustomSelect
                    list={getPresList()}
                    value={al?.name}
                    error={al?.nameErr}
                    helperText={al?.nameErr ? reqTxt : ""}
                    addNewSelect
                    mandatoryField
                    handleChange={(e) => {
                      onChange(
                        "name",
                        e?.value ?? e,
                        al?.i,
                        e?.stockId ?? null,
                      );
                    }}
                    plusBtn
                  />
                </div>
                {role === "ROLE_CLINIC" ? (
                  <div className="w20Per pr10">
                    <CustomSelect
                      list={getBatchList(al?.stockId)}
                      value={al?.batchNumber}
                      error={al?.batchErr}
                      helperText={al?.batchErr ? reqTxt : ""}
                      search
                      handleChange={(e, value) => {
                        onChange("batchNumber", value, al?.i);
                      }}
                      isBatchNumber
                    />
                  </div>
                ) : null}
                {role === "ROLE_CLINIC" ? (
                  <>
                    <div className="w15Per">
                      <Typography className="align-center-wh ml3 black22 fs14 txt-regular h40">
                        {getStockBasedQtyWithToLocation(al?.stockId)}
                        {/* {getBatchById(al?.stockId, al?.batchNumber?.id)
                            ?.quantity ?? "-"} */}
                      </Typography>
                    </div>
                    <div className="w12Per pr10">
                      <Typography className="align-center-wh ml3 black22 fs14 txt-regular h40">
                        {getBatchById(al?.stockId, al?.batchNumber?.id)
                          ?.location ?? "-"}
                      </Typography>
                      {/* <CustomSelect
                          list={getLocationsWithCategory(al?.stockId)}
                          // list={getLocationList(al?.stockId)}
                          value={al?.fromLocation}
                          error={al?.fromLocationErr}
                          helperText={al?.fromLocationErr ? reqTxt : ""}
                          select
                          handleChange={(e, value) => {
                            onChange("fromLocation", value, al?.i);
                          }}
                          // search
                          // showQuantity
                          category
                        /> */}
                    </div>
                  </>
                ) : null}
                <div
                  className={role === "ROLE_CLINIC" ? "w18Per pr10" : "w25Per"}
                >
                  <CustomTextField
                    value={al?.quantity}
                    error={al?.qtyErr}
                    helperText={al?.qtyErr ? reqTxt : ""}
                    handleChange={(e) => {
                      onChange("quantity", e?.target?.value, al?.i);
                    }}
                    type="number"
                    fullWidth
                  />
                </div>
                <div className="w5Per">
                  <div
                    className="cursor mt5"
                    onClick={() => handleDeleteItem(al?.i)}
                  >
                    <div className="flex-center">
                      <DeleteOutlineIcon sx={delBtn} />
                    </div>
                    <Typography className="red3 fs8 txt-semi-bold text-align-center">
                      Delete
                    </Typography>
                  </div>
                </div>
              </div>
            );
          })}
          <div className="flex-row-ali-cen mt5">
            <Typography className="txt-semi-bold fs14 black">Note: </Typography>
            <Typography className="font-medium fs14 ml5 gray3">
              Please note that all quantities are in subunits.
            </Typography>
          </div>
          {modValues?.location?.id ? (
            <div
              className="flex-row-ali-cen mt20 cursor"
              onClick={handleAddNew}
            >
              <div className="flex-center plus-icon">
                <AddIcon sx={addIcon} />
              </div>
              <Typography className="blue-color fs16 ml5 font-bold">
                Add
              </Typography>
            </div>
          ) : null}
          {addedList?.length > 0 ? (
            <div className="flex1-center mv10">
              <div>
                <CustomButton text="Submit" smallBtn onClick={handleSave} />
              </div>
            </div>
          ) : null}
        </div>
      </CustomModal>
    </>
  );
};

export default StockRequestModal;
